'use client';
import React, { useEffect, useRef } from 'react';

interface ProductCard {
  id: string;
  badge: string;
  badgeColor: string;
  icon: string;
  title: string;
  subtitle: string;
  description: string;
  features: string[];
  ctaText: string;
  ctaHref: string;
  accentColor: string;
}

const products: ProductCard[] = [
  {
    id: 'fleet-cap',
    badge: 'Trade Crews',
    badgeColor: '#ff6b35',
    icon: '🧢',
    title: 'The Fleet Cap.',
    subtitle: 'Zero-Crack. Zero-Compromise.',
    description:
      'Premium structured trucker hats featuring flexible 3D tactile leather patches engineered for local trade, landscaping, and construction crews. Your brand on every job site — built to last as long as your reputation.',
    features: [
      'Flexible 3D leather patch — never cracks',
      'Full-color UV-resistant print',
      'Structured front panel for maximum logo impact',
      'Minimum order: 12 units',
    ],
    ctaText: 'Explore Fleet Caps',
    ctaHref: '#fleet-cap',
    accentColor: '#ff6b35',
  },
  {
    id: 'hospitality',
    badge: 'Hospitality',
    badgeColor: '#00b4ff',
    icon: '🥃',
    title: 'The Hospitality Suite.',
    subtitle: 'Tactile Luxury. Functional Branding.',
    description:
      'Heavyweight clear acrylic coasters and vintage motel keychains featuring raised 3D glass gloss layers and embedded, functional QR codes. Built for bars, lounges, boutique hotels, and Airbnbs that refuse to be forgettable.',
    features: [
      'Raised 3D glass gloss surface layer',
      'Embedded functional QR codes',
      'Heavyweight acrylic — premium feel',
      'Custom vintage keychain designs',
    ],
    ctaText: 'Explore Hospitality Suite',
    ctaHref: '#hospitality',
    accentColor: '#00b4ff',
  },
  {
    id: 'executive',
    badge: 'Corporate Gifting',
    badgeColor: '#c0a060',
    icon: '🔧',
    title: 'Executive Utility.',
    subtitle: 'Gifts That Close Deals.',
    description:
      'Premium wood-handled multi-tools, pocket knives, and bamboo office pens customized with raised tactile grip branding. Engineered for elite corporate gifting programs that leave a lasting impression — not a landfill donation.',
    features: [
      'Raised tactile grip branding',
      'Premium wood & bamboo materials',
      'Multi-tools, knives & pen sets',
      'Custom packaging available',
    ],
    ctaText: 'Explore Executive Utility',
    ctaHref: '#executive',
    accentColor: '#c0a060',
  },
];

export default function ProductSuiteSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );

    const animatedEls = sectionRef.current?.querySelectorAll('.animate-card');
    animatedEls?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="product-suite"
      ref={sectionRef}
      className="py-20 px-4"
      style={{ backgroundColor: 'rgba(0,0,0,0)' }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="section-label">The Tactile Scale Suite</p>
          <h2
            className="font-montserrat font-black text-white mb-4"
            style={{ fontSize: 'clamp(28px, 4vw, 52px)', letterSpacing: '-1px' }}
          >
            Everything You Need to{' '}
            <span style={{ color: '#00b4ff' }}>Dominate</span>
          </h2>
          <div
            className="w-16 h-0.5 mx-auto mb-6"
            style={{ background: 'linear-gradient(90deg, #00b4ff, #00ff88)' }}
          />
          <p
            className="max-w-2xl mx-auto"
            style={{ color: 'rgb(136, 147, 152)', fontSize: '18px' }}
          >
            Three premium product ecosystems. One unified mission. Click any suite to explore what
            tactile branding can do for your business.
          </p>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {products.map((product, index) => (
            <div
              key={product.id}
              className="animate-card opacity-0 translate-y-8 transition-all duration-700 relative group"
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <a
                href={product.ctaHref}
                className="block h-full"
                style={{ textDecoration: 'none' }}
              >
                <div
                  className="relative h-full rounded-lg border overflow-hidden transition-all duration-300"
                  style={{
                    borderColor: '#1a3a5c',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
                  }}
                  onMouseEnter={(e) => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.borderColor = `${product.accentColor}66`;
                    el.style.boxShadow = `0 8px 40px ${product.accentColor}22`;
                  }}
                  onMouseLeave={(e) => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.borderColor = '#1a3a5c';
                    el.style.boxShadow = '0 4px 24px rgba(0,0,0,0.4)';
                  }}
                >
                  {/* Top accent bar */}
                  <div
                    className="absolute top-0 left-0 right-0 h-0.5 transition-all duration-300 opacity-0 group-hover:opacity-100"
                    style={{ background: `linear-gradient(90deg, transparent, ${product.accentColor}, transparent)` }}
                  />

                  <div className="p-6 flex flex-col h-full">
                    {/* Card Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {/* Icon */}
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
                        >
                          <span style={{ fontSize: '20px' }}>{product.icon}</span>
                        </div>
                        <div>
                          <h3
                            className="font-montserrat font-bold text-white text-xl leading-tight transition-colors duration-200 group-hover:text-current"
                            style={{ color: '#ffffff' }}
                          >
                            {product.title}
                          </h3>
                          <p
                            className="text-sm font-semibold mt-0.5"
                            style={{ color: product.accentColor }}
                          >
                            {product.subtitle}
                          </p>
                        </div>
                      </div>
                      {/* Badge */}
                      <span
                        className="flex-shrink-0 text-xs font-bold font-montserrat uppercase tracking-wide px-2.5 py-1 rounded-full"
                        style={{
                          backgroundColor: `${product.accentColor}1a`,
                          color: product.accentColor,
                          border: `1px solid ${product.accentColor}4d`,
                        }}
                      >
                        {product.badge}
                      </span>
                    </div>

                    {/* Description */}
                    <p
                      className="mb-5 leading-relaxed flex-grow"
                      style={{ color: 'rgb(136, 147, 152)', fontSize: '14px' }}
                    >
                      {product.description}
                    </p>

                    {/* Features */}
                    <ul className="mb-6 space-y-2">
                      {product.features.map((feature) => (
                        <li
                          key={feature}
                          className="flex items-center gap-2 text-sm"
                          style={{ color: 'rgba(255,255,255,0.7)' }}
                        >
                          <span style={{ color: product.accentColor, fontSize: '10px' }}>▶</span>
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {/* CTA Link */}
                    <div
                      className="flex items-center gap-2 font-montserrat font-bold uppercase text-sm transition-all duration-200"
                      style={{ color: product.accentColor, letterSpacing: '0.5px' }}
                    >
                      {product.ctaText}
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        className="transition-transform duration-200 group-hover:translate-x-1"
                      >
                        <path
                          d="M2 7h10M7 2l5 5-5 5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
